// Floating bug-report button + slide-up modal. Mounted globally from
// App.tsx so it appears on every page. Captures URL, user agent, screen
// size, and the logged-in user's id/email automatically; the user only
// fills in "what happened" + optional "what was expected".
//
// Mobile (<768px): button sits at bottom-80px so it clears the
// MobileBottomNav, label hidden to keep the touch target compact. Modal
// slides up from the bottom and clamps to 90vh.
//
// Desktop (>=768px): button sits bottom-right with the "Report a bug"
// text visible. Modal is a card anchored to the bottom-right corner.

import { useState } from "react";
import { useLocation } from "react-router-dom";
import { Bug, CheckCircle, Send, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

const BugReportButton = () => {
  const [open, setOpen] = useState(false);
  const [description, setDescription] = useState("");
  const [expected, setExpected] = useState("");
  const [attachment, setAttachment] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  // Page-aware positioning — booking + vehicle-detail routes carry a
  // sticky CTA bar at the bottom on mobile. Push the bug trigger up so
  // it doesn't cover the renter's primary action.
  const location = useLocation();
  const isBookingPage =
    location.pathname.includes("/book/") ||
    location.pathname.includes("/vehicle/");

  const handleSubmit = async () => {
    if (!description.trim()) {
      setError("Please describe the bug.");
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      const { data: { user } } = await supabase.auth.getUser();
      const platform = window.innerWidth < 768 ? "mobile" : "desktop";

      // Optional attachment. Upload first, then attach the public URL
      // to the bug report payload. Failures are non-blocking — we
      // would rather receive the report without the screenshot than
      // drop the whole submission because the upload hiccuped.
      let attachmentUrl: string | undefined;
      if (attachment) {
        const storagePath = `${Date.now()}_${attachment.name}`;
        const { error: uploadErr } = await supabase.storage
          .from("bug-reports")
          .upload(storagePath, attachment, {
            contentType: attachment.type || undefined,
            upsert: false,
          });
        if (uploadErr) {
          console.warn("[BugReportButton] attachment upload failed", uploadErr);
        } else {
          const { data: publicUrlData } = supabase.storage
            .from("bug-reports")
            .getPublicUrl(storagePath);
          attachmentUrl = publicUrlData?.publicUrl;
        }
      }

      const payload = {
        description: description.trim(),
        expected: expected.trim(),
        url: window.location.href,
        userAgent: navigator.userAgent,
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
        userId: user?.id || null,
        userEmail: user?.email || null,
        timestamp: new Date().toISOString(),
        platform,
        attachmentUrl,
      };

      const { error: fnError } = await supabase.functions.invoke(
        "send-bug-report",
        { body: payload },
      );

      if (fnError) throw fnError;

      setSubmitted(true);
      setDescription("");
      setExpected("");
      setAttachment(null);

      // Auto-close after 3 seconds so the user can keep using the page.
      setTimeout(() => {
        setOpen(false);
        setSubmitted(false);
      }, 3000);
    } catch (err) {
      console.error("[BugReportButton] submit failed", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (submitting) return;
    setOpen(false);
    setSubmitted(false);
    setDescription("");
    setExpected("");
    setAttachment(null);
    setError("");
  };

  return (
    <>
      {/* Floating trigger — 44px circle at rest, expands to a pill on
          hover and reveals the "Report a bug" label. All positioning is
          done from the CSS block below (not an inline style object) so
          the hover width transition, mobile bottoms, and is-booking-page
          override live in one place. Right value is 30px so the 44px
          circle is horizontally centred over the 56px SupportChatbot
          button below it (AI button right: 24, width: 56 → centre at
          52px from right; 44px bug at right: 30 → centre at 52px from
          right; same vertical axis as a result, expand-to-the-left grows
          the pill away from that axis without disturbing alignment). */}
      <button
        id="vauren-bug-report-trigger"
        className={isBookingPage ? "is-booking-page" : undefined}
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Report a bug"
        title="Report a bug"
      >
        <span className="bug-label">Report a bug</span>
        <span className="bug-icon-wrap">
          <Bug size={18} strokeWidth={1.8} color="#997E54" />
        </span>
      </button>

      {open && (
        <div
          id="vauren-bug-modal-overlay"
          onClick={handleClose}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(44,36,32,0.4)",
            backdropFilter: "blur(4px)",
            WebkitBackdropFilter: "blur(4px)",
            // 10000 — above SupportChatbot's open-chat panel (z 9999)
            // so the bug-report flow can never be hidden behind the AI
            // chat window if the renter happened to leave it open.
            zIndex: 10000,
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "flex-end",
            padding: 24,
          }}
        >
          <div
            id="vauren-bug-modal-card"
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "relative",
              zIndex: 10001,
              background: "#FFFFFF",
              borderRadius: 8,
              width: "100%",
              maxWidth: 420,
              maxHeight: "90vh",
              overflowY: "auto",
              boxShadow: "0 24px 64px rgba(44,36,32,0.2)",
            }}
          >
            <div
              style={{
                background: "#0D0D0B",
                padding: "16px 20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <Bug size={14} color="#997E54" />
                <span
                  style={{
                    fontFamily: '"DM Sans", system-ui, sans-serif',
                    fontSize: 13,
                    color: "#F5F0EB",
                    letterSpacing: "0.08em",
                  }}
                >
                  Report a bug
                </span>
              </div>
              <button
                type="button"
                onClick={handleClose}
                aria-label="Close"
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  color: "rgba(245,240,235,0.5)",
                  padding: 4,
                  display: "flex",
                  minWidth: 32,
                  minHeight: 32,
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <X size={16} />
              </button>
            </div>

            <div style={{ height: 2, background: "#997E54" }} />

            {submitted ? (
              <div style={{ padding: "40px 24px", textAlign: "center" }}>
                <CheckCircle
                  size={40}
                  color="#2C6E49"
                  style={{ margin: "0 auto 16px", display: "block" }}
                />
                <div
                  style={{
                    fontFamily: '"Cormorant Garamond", serif',
                    fontSize: 20,
                    color: "#2C2420",
                    marginBottom: 8,
                  }}
                >
                  Thank you
                </div>
                <p
                  style={{
                    fontFamily: '"DM Sans", system-ui, sans-serif',
                    fontSize: 14,
                    color: "rgba(44,36,32,0.6)",
                    lineHeight: 1.6,
                    margin: 0,
                  }}
                >
                  Your report has been received. We will look into it shortly.
                </p>
              </div>
            ) : (
              <div style={{ padding: 24 }}>
                <p
                  style={{
                    fontFamily: '"DM Sans", system-ui, sans-serif',
                    fontSize: 13,
                    color: "rgba(44,36,32,0.55)",
                    lineHeight: 1.6,
                    marginBottom: 20,
                    marginTop: 0,
                  }}
                >
                  Describe what happened and we will investigate. Your browser
                  details and current page are captured automatically.
                </p>

                <div style={{ marginBottom: 16 }}>
                  <label
                    style={{
                      display: "block",
                      fontFamily: '"DM Sans", system-ui, sans-serif',
                      fontSize: 11,
                      color: "rgba(44,36,32,0.5)",
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                      marginBottom: 8,
                    }}
                  >
                    What happened
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => {
                      setDescription(e.target.value);
                      if (error) setError("");
                    }}
                    placeholder="Describe the bug..."
                    rows={3}
                    maxLength={5000}
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      border: error
                        ? "1px solid #B5443C"
                        : "1px solid rgba(44,36,32,0.15)",
                      borderRadius: 4,
                      fontFamily: '"DM Sans", system-ui, sans-serif',
                      fontSize: 14,
                      color: "#2C2420",
                      lineHeight: 1.6,
                      resize: "vertical",
                      outline: "none",
                      background: "#FAFAF8",
                      boxSizing: "border-box",
                    }}
                  />
                  {error && (
                    <div
                      style={{
                        fontFamily: '"DM Sans", system-ui, sans-serif',
                        fontSize: 12,
                        color: "#B5443C",
                        marginTop: 4,
                      }}
                    >
                      {error}
                    </div>
                  )}
                </div>

                <div style={{ marginBottom: 20 }}>
                  <label
                    style={{
                      display: "block",
                      fontFamily: '"DM Sans", system-ui, sans-serif',
                      fontSize: 11,
                      color: "rgba(44,36,32,0.5)",
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                      marginBottom: 8,
                    }}
                  >
                    What did you expect
                    <span
                      style={{
                        color: "rgba(44,36,32,0.3)",
                        marginLeft: 6,
                      }}
                    >
                      (optional)
                    </span>
                  </label>
                  <textarea
                    value={expected}
                    onChange={(e) => setExpected(e.target.value)}
                    placeholder="What should have happened instead..."
                    rows={2}
                    maxLength={5000}
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      border: "1px solid rgba(44,36,32,0.15)",
                      borderRadius: 4,
                      fontFamily: '"DM Sans", system-ui, sans-serif',
                      fontSize: 14,
                      color: "#2C2420",
                      lineHeight: 1.6,
                      resize: "vertical",
                      outline: "none",
                      background: "#FAFAF8",
                      boxSizing: "border-box",
                    }}
                  />
                </div>

                <div style={{ marginBottom: 20 }}>
                  <label
                    style={{
                      display: "block",
                      fontFamily: '"DM Sans", system-ui, sans-serif',
                      fontSize: 11,
                      color: "rgba(44,36,32,0.5)",
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                      marginBottom: 8,
                    }}
                  >
                    Attach a screenshot
                    <span
                      style={{
                        color: "rgba(44,36,32,0.3)",
                        marginLeft: 6,
                      }}
                    >
                      (optional)
                    </span>
                  </label>
                  {/* capture="environment" gently nudges mobile browsers to
                      surface the camera roll / camera as a first-class
                      option alongside the file picker. accept covers the
                      common screenshot formats plus PDFs for system
                      diagnostic exports. */}
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    capture="environment"
                    onChange={(e) => setAttachment(e.target.files?.[0] ?? null)}
                    disabled={submitting}
                    style={{
                      width: "100%",
                      fontFamily: '"DM Sans", system-ui, sans-serif',
                      fontSize: 13,
                      color: "#2C2420",
                      padding: "8px 0",
                      cursor: submitting ? "not-allowed" : "pointer",
                    }}
                  />
                  {attachment && (
                    <p
                      style={{
                        margin: "4px 0 0",
                        fontFamily: '"DM Sans", system-ui, sans-serif',
                        fontSize: 12,
                        color: "rgba(44,36,32,0.55)",
                        lineHeight: 1.4,
                      }}
                    >
                      {attachment.name}
                    </p>
                  )}
                </div>

                <div
                  style={{
                    background: "#F5F0EB",
                    borderRadius: 4,
                    padding: "10px 14px",
                    marginBottom: 20,
                    fontFamily: '"DM Sans", system-ui, sans-serif',
                    fontSize: 12,
                    color: "rgba(44,36,32,0.5)",
                    lineHeight: 1.5,
                  }}
                >
                  Automatically captured: current page, browser, screen size,
                  and account details.
                </div>

                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={submitting}
                  style={{
                    width: "100%",
                    background: submitting
                      ? "rgba(44,36,32,0.4)"
                      : "#2C2420",
                    color: "#F5F0EB",
                    border: "none",
                    borderRadius: 2,
                    padding: "13px 24px",
                    fontFamily: '"DM Sans", system-ui, sans-serif',
                    fontSize: 12,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    cursor: submitting ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                    minHeight: 48,
                  }}
                >
                  <Send size={13} />
                  {submitting ? "Sending..." : "Send report"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* dangerouslySetInnerHTML rather than <style>{...}</style> to avoid
          the React-managed text-node removeChild crash documented in
          CookieConsent.tsx. Drives the icon-only resting state, the
          hover-expand pill, mobile bottoms, and the booking-page nudge.
          Mobile bottoms: 140 keeps the bug button clear of the AI
          button + the mobile bottom nav. is-booking-page bumps to 200
          on BookVehicle / VehicleDetail mobile routes that mount their
          own sticky bottom CTA bar. */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
            #vauren-bug-report-trigger {
              position: fixed;
              /* Pinned to the AI chat widget's safe-area calculation so
                 the two buttons move as a unit and stay exactly 12px
                 apart regardless of scroll-triggered safe-area changes
                 on mobile.
                   AI button:  bottom = 24 + env(...), height = 56
                   12px gap above it = 24 + 56 + 12 = 92
                 Was 96 — drifted 4px from the AI widget on viewports
                 where the safe-area inset is non-zero. */
              bottom: calc(92px + env(safe-area-inset-bottom));
              right: calc(30px + env(safe-area-inset-right));
              z-index: 9998;
              display: flex;
              align-items: center;
              justify-content: center;
              flex-direction: row-reverse;
              height: 44px;
              width: 44px;
              border-radius: 22px;
              padding: 0;
              background: #FFFFFF;
              border: 1px solid rgba(44,36,32,0.15);
              box-shadow: 0 2px 8px rgba(44,36,32,0.12);
              cursor: pointer;
              overflow: hidden;
              transition:
                width 0.2s ease,
                box-shadow 0.2s ease;
            }
            #vauren-bug-report-trigger:hover,
            #vauren-bug-report-trigger:focus-visible {
              width: 148px;
              box-shadow: 0 4px 16px rgba(44,36,32,0.16);
              outline: none;
            }
            #vauren-bug-report-trigger .bug-label {
              font-family: "DM Sans", system-ui, sans-serif;
              font-size: 12px;
              color: #2C2420;
              white-space: nowrap;
              opacity: 0;
              width: 0;
              overflow: hidden;
              transition:
                opacity 0.15s ease 0.05s,
                width 0.2s ease,
                padding-left 0.2s ease;
              padding-left: 0;
              letter-spacing: 0.02em;
            }
            #vauren-bug-report-trigger:hover .bug-label,
            #vauren-bug-report-trigger:focus-visible .bug-label {
              opacity: 1;
              width: auto;
              padding-left: 10px;
            }
            #vauren-bug-report-trigger .bug-icon-wrap {
              display: flex;
              align-items: center;
              justify-content: center;
              width: 44px;
              height: 44px;
              flex-shrink: 0;
            }
            @media (max-width: 767px) {
              #vauren-bug-report-trigger {
                /* Same 12px-above-AI-button math as desktop so the
                   pair tracks together through scroll + safe-area
                   recalculations. */
                bottom: calc(92px + env(safe-area-inset-bottom));
                right: calc(30px + env(safe-area-inset-right));
              }
              #vauren-bug-report-trigger.is-booking-page {
                /* Booking + vehicle-detail routes carry a sticky CTA
                   on mobile. Bump 68px above the standard mobile
                   position so the bug trigger clears the reserve bar. */
                bottom: calc(160px + env(safe-area-inset-bottom));
              }
              #vauren-bug-modal-overlay {
                padding: 0 !important;
              }
              #vauren-bug-modal-card {
                border-radius: 12px 12px 0 0 !important;
                max-width: 100% !important;
                width: 100% !important;
              }
            }
          `,
        }}
      />
    </>
  );
};

export default BugReportButton;
